import { redirect, type MetaFunction } from "@remix-run/react"
import {
  articleBodyStyle,
  articleSection,
  h1Style,
  h1Wrap,
  h2Style,
  headerTextStyle,
  stepSectionStyle,
} from "./article.css"
import Footer from "~/components/Footer"
import { page } from "~/shared/styles.css"
import Logo from "~/components/Logo"
import { btnWrap, masonryItem, masonryWrap } from "./index.css"
import LinkButton from "~/components/LinkButton"
import ScrollyWolly from "~/components/ScrollyWolly"
import Link from "~/components/Link"
import StickyHeader from "~/components/StickyHeader"

const masonryImgUrls = [
  {
    url: "/landing/grid/2fd1277d-00c4-460a-ab30-a9901cf3e1251.webp",
    alt: "AI generated Tropical Oasis flower patch",
  },
  {
    url: "/landing/grid/5cffdd1c-7ee5-4667-b39a-cb3681fad70f3.webp",
    alt: "Modern and wildlife fusion flower patch, instant AI design ideas",
  },
  {
    url: "/landing/grid/cozy-balcony-ai-generated.webp",
    alt: "Cozy balcony, reimagined with Rescape AI",
  },
  {
    url: "/landing/grid/c20b1846-d4bd-442b-abf6-aa9f1074b9342.webp",
    alt: "Calm serene garden with rocking chair",
  },
  {
    url: "/landing/grid/f2d0575e-8e19-4002-8109-970f4b561c3e2.webp",
    alt: "Fantastical and grand pergola from ai backyard design generator",
  },
]

export type PreviewImage = {
  url: string
  size: { w: number; h: number }
}

export type Url = { url: string; initial?: PreviewImage | null; loading?: boolean }

// SEO FIX: Title capitalised properly (was "AI backyard design | A New Realm For Backyard Design")
const TITLE = "AI Backyard Design | Transform Your Outdoor Space"
// SEO FIX: Description shortened from 161 to 152 chars (was over 155 char limit)
const DESCRIPTION =
  "Transform backyard and garden design with AI. Experience personalised backyard makeovers effortlessly. Perfect for homeowners, DIY enthusiasts and landscapers."
const IMAGE = "https://rescapeai.com/articles/ai-yard-design-patio-and-flowerbeds.webp"

export const meta: MetaFunction = () => {
  return [
    {
      title: TITLE,
    },
    {
      name: "description",
      content: DESCRIPTION,
    },
    {
      property: "og:title",
      content: TITLE,
    },
    {
      property: "og:description",
      content: DESCRIPTION,
    },
    {
      property: "og:image",
      content: IMAGE,
    },
    // SEO FIX: Added Twitter Card tags
    {
      name: "twitter:card",
      content: "summary_large_image",
    },
    {
      name: "twitter:title",
      content: TITLE,
    },
    {
      name: "twitter:description",
      content: DESCRIPTION,
    },
    {
      name: "twitter:image",
      content: IMAGE,
    },
    {
      tagName: "link",
      rel: "canonical",
      href: "https://rescapeai.com/ai-backyard-design",
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: TITLE,
        description: DESCRIPTION,
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2024-07-06",
        dateModified: "2024-07-06",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: IMAGE,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": "https://rescapeai.com/ai-backyard-design",
        },
        articleSection: [
          "Gardening",
          "Landscape Design",
          "Artificial Intelligence",
          "Technology",
        ],
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/articles/ai-yard-design-patio-and-flowerbeds.webp)",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>
            AI Backyard Design - Rescape: A Changing Landscape
          </h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />
      <div className={page}>
        <section className={stepSectionStyle}>
          <p className={articleBodyStyle}>
            Imagine transforming your backyard into a stunning oasis with just a few
            clicks. Welcome to the future of landscaping with Rescape AI. This
            cutting-edge AI landscape generator empowers users to redesign their yards and
            gardens effortlessly, offering a blend of creativity and precision like never
            before. Discover how Rescape AI can benefit various users and revolutionize
            landscaping and backyard design.
          </p>
        </section>
        <ScrollyWolly
          before={{
            image: "/articles/before-ai-yard-design-patio.webp",
            title: "Your Backyard",
            alt: "Large concrete slabs and a lone tree create an uninviting patio",
          }}
          after={{
            image: "/articles/ai-yard-design-patio-and-flowerbeds.webp",
            title: "AI generated",
            alt: "Imagining a different environment, the model has created an inviting space featuring irregular paving, charming small-petaled flowers, and a cast iron seating area, making it a perfect spot for relaxation.",
          }}
          height="478px"
        />
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Personalized AI Backyard Makeovers</h2>
          <p className={articleBodyStyle}>
            For homeowners dreaming of a personalized backyard makeover, Rescape AI is a
            game-changer. By{" "}
            <Link href="/article/contemporary-garden-ideas-with-rescape-ai">
              simply uploading an image
            </Link>{" "}
            of your current backyard, you can explore a variety of design styles—from
            lush, tropical paradises to minimalist zen gardens. The AI landscape generator
            considers your existing layout and provides tailored design suggestions. Plus,
            you can select specific areas to remain unchanged, ensuring your favorite
            elements stay intact.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Streamlining Landscape Architecture Projects</h2>
          <p className={articleBodyStyle}>
            Landscape architects often face challenges in visualizing and communicating
            designs to clients. Rescape AI simplifies this process by providing detailed,
            AI-generated designs based on real project site images. Architects can use
            these as a foundation, customizing further to meet specific client needs. This
            tool accelerates the initial design phase, allowing architects to focus on
            refining and perfecting the final output.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Efficient Yard Maintenance Planning</h2>
          <p className={articleBodyStyle}>
            Garden maintenance businesses can streamline their operations with Rescape AI.
            Planning and executing yard work becomes more efficient with precise, visually
            appealing design suggestions. Business users can upload client yard images and
            receive AI-generated plans highlighting areas needing attention, new design
            implementations, or seasonal adjustments. This leads to organized and
            effective maintenance schedules, ultimately resulting in satisfied customers.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Creating Community Spaces</h2>
          <p className={articleBodyStyle}>
            Municipalities and community organizations can use Rescape AI to design and
            improve public spaces like parks, community gardens, and playgrounds. By
            generating multiple design options, Rescape AI helps create aesthetically
            pleasing and functional community spaces. Engaging the community in the design
            process by showcasing AI-generated options fosters a sense of ownership and
            pride in the resulting spaces.
          </p>
        </section>
        <div className={btnWrap} style={{ marginTop: `${3}rem` }}>
          <LinkButton url="/hello/signup" shouldPrefetch>
            Start AI Backyard design
          </LinkButton>
        </div>
        <section className={articleSection}>
          <div className={masonryWrap}>
            {masonryImgUrls.map(({ url, alt }) => (
              <img
                key={url}
                src={url}
                alt={alt}
                className={masonryItem}
                style={{ width: "100%", height: "auto", borderRadius: "6px" }}
                loading="lazy"
              />
            ))}
          </div>
        </section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>Conclusion</h2>
          <p className={articleBodyStyle}>
            Rescape AI is revolutionizing landscaping and backyard design, offering a
            versatile and powerful tool for a wide range of users. Whether you're a
            homeowner looking to refresh your garden, a real estate agent aiming to boost
            property sales, a landscape architect streamlining your workflow, or a
            business enhancing service offerings, <Link href="/">Rescape AI</Link>{" "}
            provides the innovation and creativity needed to transform any outdoor space.
            Embrace the future of landscaping with Rescape AI and discover endless design
            possibilities for your yard and garden.
          </p>
        </section>

        <div className={btnWrap} style={{ marginTop: `${3}rem` }}>
          <LinkButton url="/hello/signup" shouldPrefetch>
            Transform you backyard
          </LinkButton>
        </div>
        <Footer />
      </div>
    </>
  )
}

export default Index
